import React, {useState} from 'react';
import {ExportItem} from './ExportItem';
import {Pagination} from './Pagination';
import {MarketReportViewer} from './MarketReportViewer';
import {FunnelIcon, XMarkIcon} from '@heroicons/react/24/outline';
import { SLIDE_OVER_TOP_OFFSET_STYLE } from '../../../../../components/slideOverTransition';

/**
 * Exports list display component
 */
export const ExportsList = ({
  sortedDisplay,
  availableResources,
  exportsPage,
  setExportsPage,
  exportsPerPage,
  setExportsPerPage,
  exportsFilterType,
  setExportsFilterType,
  onDownload,
  onRefresh,
  isRefreshing,
  isAdmin,
  isMarketOwner
}) => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);
  const [viewingResourceName, setViewingResourceName] = useState('');

  // Handle viewing a market report
  const handleViewReport = (item, resourceName) => {
    setViewingReport(item);
    setViewingResourceName(resourceName);
  };

  const handleCloseViewer = () => {
    setViewingReport(null);
    setViewingResourceName('');
  };

  // Filter exports by type
  const filteredExports = sortedDisplay.filter(item => {
    if (exportsFilterType === 'all') return true;
    const itemType = item.export_type || 'historical_forecast';

    // Treat 'individual_forecast' and 'historical_forecast' as the same type
    if (exportsFilterType === 'historical_forecast') {
      return itemType === 'historical_forecast' || itemType === 'individual_forecast';
    }

    // Handle market report variations (market, market_report, etc.)
    if (exportsFilterType === 'market') {
      return itemType === 'market' || itemType === 'market_report' || itemType.includes('market');
    }

    // Raw measurement exports
    if (exportsFilterType === 'raw_measurement') {
      return itemType === 'raw_measurement';
    }

    // Handle user activity / platform logs
    if (exportsFilterType === 'user_activity') {
      return itemType === 'user_activity';
    }

    return itemType === exportsFilterType;
  });

  const filterTypeLabels = {
    all: 'All Types',
    historical_forecast: 'Historical Forecast',
    market: 'Market Report',
    raw_measurement: 'Raw Measurement Data',
    user_activity: 'Platform Logs',
  };

  // Check if user can see platform logs filter
  const canViewPlatformLogs = isAdmin || isMarketOwner;

  return (
    <div className="rounded-lg bg-surface dark:bg-gray-870 p-4 sm:p-6 shadow-sm border border-gray-200/70 dark:border-gray-600/70 flex flex-col flex-1 min-h-[700px]">
      {/* Mobile: Stacked header */}
      <div className="sm:hidden mb-3 space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Requested files
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Download previously requested data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh exports list"
          >
            <svg
              className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button
            onClick={() => setIsFilterPanelOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <FunnelIcon className="h-5 w-5" />
            <span className="text-sm">{filterTypeLabels[exportsFilterType]}</span>
          </button>
          <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
            {filteredExports.length} req{filteredExports.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Desktop: Original header */}
      <div className="hidden sm:flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Requested files
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Download previously requested data
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh exports list"
          >
            <svg
              className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          <select
            value={exportsFilterType}
            onChange={(e) => {
              setExportsFilterType(e.target.value);
              setExportsPage(1);
            }}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="all">All Types</option>
            <option value="historical_forecast">Historical Forecast</option>
            <option value="market">Market Report</option>
            <option value="raw_measurement">Raw Measurement Data</option>
            {canViewPlatformLogs && <option value="user_activity">Platform Logs</option>}
          </select>
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {filteredExports.length} request{filteredExports.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Filter Side Panel (Mobile) */}
      {isFilterPanelOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsFilterPanelOpen(false)} />
          <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white dark:bg-gray-870 shadow-xl z-50 overflow-y-auto" style={SLIDE_OVER_TOP_OFFSET_STYLE}>
            <div className="sticky top-0 bg-surface dark:bg-gray-870 border-b border-gray-200/70 dark:border-gray-600/70 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filter Exports</h2>
              <button
                onClick={() => setIsFilterPanelOpen(false)}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Export Type
                </label>
                <div className="space-y-2">
                  <div
                    onClick={() => {
                      setExportsFilterType('all');
                      setExportsPage(1);
                      setIsFilterPanelOpen(false);
                    }}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      exportsFilterType === 'all'
                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                        : 'border-gray-200/70 dark:border-gray-600/70 hover:border-brand-300 dark:hover:border-brand-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">All Types</div>
                      {exportsFilterType === 'all' && (
                        <div className="h-5 w-5 rounded-full bg-brand-600 flex items-center justify-center">
                          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Show all export types
                    </div>
                  </div>

                  <div
                    onClick={() => {
                      setExportsFilterType('historical_forecast');
                      setExportsPage(1);
                      setIsFilterPanelOpen(false);
                    }}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      exportsFilterType === 'historical_forecast'
                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                        : 'border-gray-200/70 dark:border-gray-600/70 hover:border-brand-300 dark:hover:border-brand-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Historical Forecast</div>
                      {exportsFilterType === 'historical_forecast' && (
                        <div className="h-5 w-5 rounded-full bg-brand-600 flex items-center justify-center">
                          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Historical forecast data exports
                    </div>
                  </div>

                  <div
                    onClick={() => {
                      setExportsFilterType('market');
                      setExportsPage(1);
                      setIsFilterPanelOpen(false);
                    }}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      exportsFilterType === 'market'
                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                        : 'border-gray-200/70 dark:border-gray-600/70 hover:border-brand-300 dark:hover:border-brand-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Market Report</div>
                      {exportsFilterType === 'market' && (
                        <div className="h-5 w-5 rounded-full bg-brand-600 flex items-center justify-center">
                          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Market report data exports
                    </div>
                  </div>

                  {canViewPlatformLogs && (
                    <div
                      onClick={() => {
                        setExportsFilterType('user_activity');
                        setExportsPage(1);
                        setIsFilterPanelOpen(false);
                      }}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        exportsFilterType === 'user_activity'
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                          : 'border-gray-200/70 dark:border-gray-600/70 hover:border-brand-300 dark:hover:border-brand-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Platform Logs</div>
                        {exportsFilterType === 'user_activity' && (
                          <div className="h-5 w-5 rounded-full bg-brand-600 flex items-center justify-center">
                            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        User activity and platform logs
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Refresh Hint */}
      {sortedDisplay.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>Click the refresh button to check if your export status has changed</span>
          </p>
        </div>
      )}

      <div className="flex flex-col flex-1">
        {filteredExports.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500">
            {sortedDisplay.length === 0 ? 'No export requests yet' : 'No exports match the selected filter'}
          </div>
        ) : (
          <>
            <div className="space-y-2 flex-1 min-h-[520px]">
              {filteredExports
                .slice((exportsPage - 1) * exportsPerPage, exportsPage * exportsPerPage)
                .map((it, idx) => {
                  const resId = it.resource_id || it.resource || it.resourceId;
                  const resourceName = (availableResources && availableResources.find(r => String(r.id) === String(resId))?.name) || 'Platform';

                  return (
                    <ExportItem
                      key={it.id || idx}
                      item={it}
                      resourceName={resourceName}
                      onDownload={onDownload}
                      onView={(item) => handleViewReport(item, resourceName)}
                    />
                  );
                })}
            </div>

            {filteredExports.length > 0 && (
              <Pagination
                currentPage={exportsPage}
                totalItems={filteredExports.length}
                itemsPerPage={exportsPerPage}
                onPageChange={setExportsPage}
                onItemsPerPageChange={(value) => {
                  setExportsPerPage(value);
                  setExportsPage(1);
                }}
                itemsPerPageOptions={[5, 10, 20, 50]}
              />
            )}
          </>
        )}
      </div>

      {/* Market Report Viewer Modal */}
      {viewingReport && (
        <MarketReportViewer
          item={viewingReport}
          resourceName={viewingResourceName}
          onClose={handleCloseViewer}
          onDownload={onDownload}
        />
      )}
    </div>
  );
};

